import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown } from 'lucide-react';

export interface Institution {
  id: string;
  name: string;
  subdomain?: string;
  status?: string;
}

interface InstitutionSelectorProps {
  value: string;
  onChange: (institutionId: string, institutionName: string) => void;
  institutions: Institution[];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
}

export const InstitutionSelector: React.FC<InstitutionSelectorProps> = ({
  value,
  onChange,
  institutions,
  placeholder = "Sélectionner une institution",
  required = false,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInstitution, setSelectedInstitution] = useState<Institution | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter institutions based on search term
  const filteredInstitutions = institutions.filter(inst =>
    inst.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inst.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inst.subdomain?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Update selected institution when value changes
  useEffect(() => {
    if (value) {
      const found = institutions.find(inst => inst.id === value);
      setSelectedInstitution(found || null);
    } else {
      setSelectedInstitution(null);
    }
  }, [value, institutions]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (institution: Institution) => {
    setSelectedInstitution(institution);
    onChange(institution.id, institution.name);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Selected value display / trigger */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full px-3 py-2 border rounded-md text-left flex items-center justify-between
          ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:border-blue-400'}
          ${isOpen ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300'}
          focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200
        `}
      >
        <span className={selectedInstitution ? 'text-gray-900' : 'text-gray-400'}>
          {selectedInstitution ? (
            <div>
              <div className="font-medium">{selectedInstitution.name}</div>
              <div className="text-xs text-gray-500">{selectedInstitution.id}</div>
            </div>
          ) : (
            placeholder
          )}
        </span>
        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher par nom, ID, ou sous-domaine..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 text-sm"
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          </div>

          {/* Institution list */}
          <div className="max-h-64 overflow-y-auto">
            {filteredInstitutions.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                Aucune institution trouvée
              </div>
            ) : (
              filteredInstitutions.map((institution) => (
                <button
                  key={institution.id}
                  type="button"
                  onClick={() => handleSelect(institution)}
                  className={`
                    w-full px-4 py-2 text-left hover:bg-blue-50 transition-colors
                    ${selectedInstitution?.id === institution.id ? 'bg-blue-100' : ''}
                  `}
                >
                  <div className="font-medium text-gray-900">{institution.name}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {institution.subdomain && <span className="mr-2">{institution.subdomain}</span>}
                    <span className="font-mono">{institution.id}</span>
                  </div>
                  {institution.status && (
                    <span className={`
                      inline-block mt-1 px-2 py-0.5 text-xs rounded-full
                      ${institution.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}
                    `}>
                      {institution.status}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
